import React from 'react';
import { Link } from 'react-router-dom';

const IS_URL = /^(?:https?:\/\/|www\.)/i;
const IS_MENTION = /^@[A-Za-z0-9._-]{3,30}$/;
const URL_SPLIT = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
// "@username" — letters, digits, dot, dash, underscore (the signup rules).
const MENTION_SPLIT = /(@[A-Za-z0-9._-]{3,30})/g;

const pretty = (url) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/**
 * Renders post / comment text:
 *  - every URL becomes a blue clickable link (opens in a new tab)
 *  - **@username mentions** become links to that member's profile
 *  - line breaks are preserved (multiple new lines supported)
 *  - long descriptions can be clamped with `clamp` lines
 */
export default function Linkify({ text, className = '', clamp = 0, onMore, moreLabel }) {
  const body = String(text ?? '');
  if (!body) return null;

  const clampStyle = clamp
    ? { display: '-webkit-box', WebkitLineClamp: clamp, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
    : undefined;

  const renderMentions = (chunk) => {
    const pieces = chunk.split(MENTION_SPLIT);
    return pieces.map((piece, j) => {
      if (IS_MENTION.test(piece)) {
        return (
          <Link
            key={`m${j}`}
            to={`/u/${piece.slice(1)}`}
            className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {piece}
          </Link>
        );
      }
      return <React.Fragment key={`t${j}`}>{piece}</React.Fragment>;
    });
  };

  return (
    <p className={`whitespace-pre-wrap break-words leading-relaxed ${className}`} style={clampStyle}>
      {body.split(URL_SPLIT).map((part, i) => {
        if (!part) return null;
        if (IS_URL.test(part)) {
          const href = part.startsWith('www.') ? `https://${part}` : part;
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 dark:text-blue-400 font-medium hover:underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {pretty(part).length > 42 ? `${pretty(part).slice(0, 42)}…` : pretty(part)}
            </a>
          );
        }
        return <React.Fragment key={i}>{renderMentions(part)}</React.Fragment>;
      })}
      {clamp && onMore ? (
        <button type="button" onClick={onMore} className="ml-1 font-semibold" style={{ color: '#0B5FDB' }}>
          {moreLabel}
        </button>
      ) : null}
    </p>
  );
}
